import React, { memo, useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';
import Image from 'next/image';

import { icon_basket } from '@gn-audio/icons';
import {
  Button,
  EcomOnly,
  isNotNil,
  JabraIcon,
  TrackingContext,
  useBasket,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import {
  AvailabilityState,
  FamilyGroup,
  GtmEventType,
  GtmPayload,
  ProductType,
} from '@gn-audio/jss-core';

import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { PIMProduct } from 'api/productCatalogueApi';
import { CalloutMessage } from 'composables/Product/Buy/CalloutMessage/CalloutMessage';
import { getAvailabilityState } from 'composables/Product/Buy/customizerHelpers';
import { Price } from 'composables/Product/Buy/Price/Price';
import {
  CombinedResults,
  ISFCCGroupPrice,
} from 'composables/Product/ProductFilterModule/productFilterModuleTypes';

const COLOR_ANIMATION_STYLES = 'transition-colors ease-linear duration-300';
export type ProductFilterItemProps = CombinedResults & {
  product: CombinedResults;
  translations: ReturnType<typeof useFallbackTranslation>;
};
const ProductFilterItemComponent = ({
  product,
  translations,
}: ProductFilterItemProps): React.ReactElement => {
  const { sfccPricesData, productCatalogueLabels, productCatalogueDetails } = product;
  const { t, getURLWithLocalePrefix } = translations;

  const { addProducts } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);

  const groupPrice = sfccPricesData || ({} as ISFCCGroupPrice);
  const { images, groupId, familyId } = productCatalogueLabels || ({} as FamilyGroup);
  const { pageUrl, uniqueSellingPoint, summary, description, title } =
    productCatalogueDetails || ({} as PIMProduct);

  const standardPriceModel = mapToStandardPriceModel(groupPrice);
  const availability = getAvailabilityState(standardPriceModel);
  const isSoldOut =
    isNotNil(availability) && availability >= AvailabilityState.WhereToBuy && groupPrice;

  const [isLoadingSpinnerOn, setIsLoadingSpinnerOn] = useState(false);
  const fireAddToCartEventAndAddToBasket = useCallback(async () => {
    const { productCatalogueLabels, sfccPricesData, productCatalogueDetails } = product;
    if (!productCatalogueLabels || !sfccPricesData) return;

    const groupPrice = sfccPricesData || ({} as ISFCCGroupPrice);
    const listedPrice = groupPrice?.c_listedPrice;
    const discountedPrice = groupPrice?.c_discountedPrice;

    const discountAmount =
      groupPrice?.c_isDiscounted &&
      listedPrice != null &&
      discountedPrice != null &&
      listedPrice > discountedPrice
        ? Math.round((listedPrice - discountedPrice) * 100) / 100
        : 0;

    const eCommercePayload: GtmPayload = {
      event: GtmEventType.AddToCart,
      ecommerce: {
        currency: groupPrice?.currency || '',
        ...(discountedPrice != null && { value: discountedPrice }),
        items: [
          {
            item_name: productCatalogueDetails?.title || '',
            item_id: groupPrice?.c_sku || '',
            ...(discountedPrice != null && { price: discountedPrice }),
            item_brand: groupPrice?.brand || '',
            item_category: groupPrice?.c_productType || '',
            quantity: 1,
            discount: discountAmount,
          },
        ],
      },
    };
    if (!groupPrice?.c_sku) return;

    setIsLoadingSpinnerOn(true);
    gtmEvent(eCommercePayload);
    try {
      await addProducts([{ sku: groupPrice?.c_sku }]);
    } finally {
      setIsLoadingSpinnerOn(false);
    }
  }, [addProducts, gtmEvent, product]);

  const localizedProductPageUrl = pageUrl
    ? getURLWithLocalePrefix(`${pageUrl}?sku=${productCatalogueLabels?.sku}`)
    : undefined;

  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isCardFocused, setIsCardFocused] = useState(false);

  const { c_calloutMsg, c_couponCalloutMessage } = groupPrice || {};

  return (
    <section
      className={classNames(
        'light group/card relative flex h-full flex-col rounded-lg border-gray-100 bg-white shadow-md',
        COLOR_ANIMATION_STYLES
      )}
    >
      <a
        href={localizedProductPageUrl}
        className="absolute z-10 h-full w-full rounded-lg"
        aria-labelledby={`learn-more-product-${groupId || familyId}`}
        onMouseEnter={() => setIsCardHovered(true)}
        onMouseLeave={() => setIsCardHovered(false)}
        onFocus={() => setIsCardFocused(true)}
        onBlur={() => setIsCardFocused(false)}
      />
      <div className="relative flex h-[350px] justify-center rounded-t-lg bg-[#f5f5f5]">
        <CalloutMessage calloutMsg={c_calloutMsg} couponCalloutMessage={c_couponCalloutMessage} />
        {!!images?.[0]?.url && (
          <Image
            src={`https:${images[0].url}`}
            fill
            alt=""
            className="h-full w-full object-contain"
          />
        )}
      </div>

      <div className="flex flex-grow flex-col px-6 py-8">
        <span
          className={classNames(
            'heading-6 text-black group-hover/card:underline dark:text-white',
            COLOR_ANIMATION_STYLES
          )}
        >
          {title}
        </span>
        <span
          className={classNames('paragraph line-clamp-4 pt-2', COLOR_ANIMATION_STYLES)}
          dangerouslySetInnerHTML={{
            __html: sanitize(uniqueSellingPoint || summary || description || ''),
          }}
        />
        <div className="mt-auto">
          <div className="my-4 flex justify-end">
            {!isSoldOut && (
              <Price
                className="bg-white"
                price={groupPrice && standardPriceModel}
                productType={ProductType.Group}
              />
            )}
          </div>
          {!!pageUrl && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                id={`learn-more-product-${groupId || familyId}`}
                asLink={true}
                href={localizedProductPageUrl}
                variant={isCardHovered || isCardFocused ? 'tertiary' : 'link'}
                size="s"
                noKeyboard
                aria-label={`${t('system.LearnMoreAbout')} ${title}`}
              >
                {t('system.LearnMore')}
              </Button>
              <EcomOnly>
                {isNotNil(availability) &&
                  [
                    AvailabilityState.Add,
                    AvailabilityState.BackOrder,
                    AvailabilityState.PreOrder,
                  ].includes(availability) &&
                  groupPrice?.c_discountedPrice && (
                    <Button
                      busy={isLoadingSpinnerOn}
                      variant="primary"
                      size="s"
                      iconPlacement="right"
                      icon={<JabraIcon Icon={icon_basket} />}
                      onClick={fireAddToCartEventAndAddToBasket}
                      aria-label={`${t('system.Buy')} ${title}`}
                      className="z-20"
                    >
                      {availability === AvailabilityState.Add && <span>{t('system.Buy')}</span>}
                      {availability === AvailabilityState.BackOrder && (
                        <span>{t('system.Backorder')}</span>
                      )}
                      {availability === AvailabilityState.PreOrder && (
                        <span> {t('system.Preorder')}</span>
                      )}
                    </Button>
                  )}
              </EcomOnly>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const ProductFilterItem = memo(ProductFilterItemComponent);
